import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, TOKEN_KEY } from "@/lib/api";
import { useT, Lang } from "@/lib/i18n";

export type Owner = { id: string; phone?: string; phone_verified?: boolean; name: string; email?: string; owner_code: string; lang: string; onboarded: boolean; is_demo?: boolean };
export type Property = {
  id: string; name: string; address: string; rent_due_day: number; proration_mode: "prorated" | "full";
  id_upload_enabled: boolean; beds_total: number; beds_occupied: number; rooms_total: number;
};

type Ctx = {
  owner: Owner | null | undefined;
  login: (token: string, owner: Owner) => void;
  logout: () => void;
  setOwner: (o: Owner) => void;
  properties: Property[] | null;
  property: Property | null;
  selectProperty: (id: string) => void;
  reloadProperties: () => Promise<Property[]>;
};
const AuthCtx = createContext<Ctx>(null as any);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [owner, setOwner] = useState<Owner | null | undefined>(undefined);
  const [properties, setProperties] = useState<Property[] | null>(null);
  const [selected, setSelected] = useState<string | null>(localStorage.getItem("pg_property"));
  const { setLang } = useT();

  useEffect(() => {
    if (owner?.lang) setLang(owner.lang as Lang);
  }, [owner?.lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadProperties = useCallback(async () => {
    const r = await api.get("/properties");
    setProperties(r.data);
    return r.data as Property[];
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setOwner(null);
      return;
    }
    api.get("/auth/me").then((r) => setOwner(r.data)).catch(() => setOwner(null));
  }, []);

  useEffect(() => {
    if (owner) reloadProperties();
  }, [owner?.id, reloadProperties]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = (token: string, o: Owner) => {
    localStorage.setItem(TOKEN_KEY, token);
    setOwner(o);
  };
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("pg_property");
    setProperties(null);
    setOwner(null);
  };
  const selectProperty = (id: string) => {
    localStorage.setItem("pg_property", id);
    setSelected(id);
  };
  const property = properties?.find((p) => p.id === selected) || properties?.[0] || null;

  return (
    <AuthCtx.Provider value={{ owner, login, logout, setOwner, properties, property, selectProperty, reloadProperties }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
